import type { ReactNode } from 'react';
import { NAVY } from 'styles/tokens';

const textToReactNodes = (text?: string | null): ReactNode[] => {
  if (!text) {
    return [];
  }
  const specialTextsRegex =
    /(```[\s\S]+?```|\|[^\n]*\|(?:\n[ \t]*\|[^\n]*\|)*|\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s",\\)]+(?<![.,;:!?])|<color:[^>]+>.*?<\/color>|(?<!\w)\*+\S.+?\S\*+(?!\w)|(?<!\w)_\S.+?\S_(?!\w)|(?<!\w)~+\S.+?\S~(?!\w)|`.+?`)/g;
  const nodes: ReactNode[] = text
    .replaceAll('&#10;', '\n')
    .trim()
    .split(specialTextsRegex)
    .map((textSegment, index) => {
      // Handle markdown links [text](url)
      const markdownLinkMatch = textSegment.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (markdownLinkMatch) {
        const [, linkText, linkUrl] = markdownLinkMatch;
        return (
          <a
            href={linkUrl}
            key={`link-${index + 1}`}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none', color: '#1976d2' }}
          >
            {linkText}
          </a>
        );
      }
      if (textSegment.startsWith('http')) {
        return (
          <a
            href={textSegment}
            key={`${textSegment}-${index + 1}`}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            {textSegment}
          </a>
        );
      }
      // Handle color tags <color:colorValue>text</color>
      if (textSegment.startsWith('<color:')) {
        const colorMatch = textSegment.match(/^<color:([^>]+)>(.*?)<\/color>$/);
        if (colorMatch) {
          const [, color, coloredText] = colorMatch;
          return (
            <span key={`color-${color}-${index + 1}`} style={{ color }}>
              {coloredText}
            </span>
          );
        }
        return textSegment;
      }
      if (textSegment.startsWith('```') && textSegment.endsWith('```')) {
        const lines = textSegment.slice(3, -3).trimEnd().split('\n');
        // find the shortest initial white spaces in all lines
        const minSpaces = Math.min(...lines.slice(1).map((line) => line.match(/^\s*/)?.[0].length || 0));
        // remove the shortest initial white spaces from all lines
        const linesWithoutInitialSpaces = lines.map((line, i) => (i === 0 ? line : line.slice(minSpaces).trimEnd()));
        return (
          <div
            key={`${textSegment}-${index + 1}`}
            style={{ color: NAVY, borderColor: '#d4d4d4', backgroundColor: '#f9f9f9', borderRadius: '0.375rem' }}
          >
            <div
              style={{
                width: '100%',
                fontSize: '0.8rem',
                padding: '8px 16px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ color: '#5d5d5d', fontSize: '12px' }}>{lines[0] || 'Bloque de código'}</span>
              <button
                style={{
                  color: '#5d5d5d',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  fontSize: '12px',
                }}
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(lines.slice(1).join('\n')).catch(console.error);
                }}
              >
                Copiar
              </button>
            </div>
            <div style={{ width: '100%', padding: '0 16px 16px 16px' }}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{linesWithoutInitialSpaces.slice(1).join('\n')}</span>
            </div>
          </div>
        );
      }
      // Handle markdown tables
      if (textSegment.startsWith('|') && textSegment.includes('\n')) {
        const lines = textSegment.trim().split('\n');
        const tableLines = lines.filter((line) => line.trim().startsWith('|') && line.trim().endsWith('|'));

        if (tableLines.length >= 2) {
          // Parse header
          const headerCells = tableLines[0]
            .split('|')
            .slice(1, -1)
            .map((cell) => cell.trim());

          // Skip separator line (line with dashes)
          const dataRows = tableLines.slice(2).map((row) =>
            row
              .split('|')
              .slice(1, -1)
              .map((cell) => cell.trim())
          );

          return (
            <div
              key={`table-${index + 1}`}
              style={{
                overflowX: 'auto',
                margin: '16px 0',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                color: NAVY,
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    {headerCells.map((header, headerIndex) => (
                      <th
                        key={`header-${header.replace(/\s+/g, '-')}-col-${headerCells.length}`}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          borderBottom: '2px solid #e0e0e0',
                          borderRight: headerIndex < headerCells.length - 1 ? '1px solid #e0e0e0' : 'none',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, rowIndex) => (
                    <tr
                      key={`row-${row.join('-').replace(/\s+/g, '-')}-${rowIndex + 1}`}
                      style={{
                        backgroundColor: dataRows.indexOf(row) % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {row.map((cell, cellIndex) => (
                        <td
                          key={`row-${rowIndex + 1}-cell-${cell.replace(/\s+/g, '-')}-${cellIndex + 1}`}
                          style={{
                            padding: '12px 16px',
                            borderBottom: dataRows.indexOf(row) < dataRows.length - 1 ? '1px solid #e0e0e0' : 'none',
                            borderRight: row.indexOf(cell) < row.length - 1 ? '1px solid #e0e0e0' : 'none',
                            verticalAlign: 'top',
                            minWidth: '100px',
                          }}
                        >
                          {textToReactNodes(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      }
      if (textSegment.startsWith('`') && textSegment.endsWith('`')) {
        // use mono font for code
        return (
          <code
            key={`${textSegment}-${index + 1}`}
            style={{ color: NAVY, backgroundColor: '#ececec', padding: '.15rem .3rem', borderRadius: '.25rem' }}
          >
            {textSegment.slice(1, -1)}
          </code>
        );
      }
      if (textSegment.startsWith('*') && textSegment.endsWith('*')) {
        return (
          <strong key={`${textSegment}-${index + 1}`}>{textToReactNodes(textSegment.replace(/^\*+|\*+$/g, ''))}</strong>
        );
      }
      if (textSegment.startsWith('_') && textSegment.endsWith('_')) {
        return <em key={`${textSegment}-${index + 1}`}>{textToReactNodes(textSegment.replace(/^_+|_+$/g, ''))}</em>;
      }
      if (textSegment.startsWith('~') && textSegment.endsWith('~')) {
        return <del key={`${textSegment}-${index + 1}`}>{textToReactNodes(textSegment.replace(/^~+|~+$/g, ''))}</del>;
      }
      return (
        <span key={`text-${index + 1}`} style={{ whiteSpace: 'pre-wrap' }}>
          {textSegment}
        </span>
      );
    });
  return nodes;
};

export default textToReactNodes;
